import { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { captureCharacterizationPdf } from "../core/utils/capturePdf";
import { pdf } from "@react-pdf/renderer";
import { getAzureBlobFile } from "../../../services/container";
import { mergePdfs } from "../../../components/download/pdfUtils";
import { toast } from "react-toastify";
import { getCharacterizationStatus } from "../core/utils/utils";
import { getCharacterizationDetailBackTarget } from "../core/utils/characterizationViewMode";
import { exportCsvFromRow } from "../core/utils/exportCsvFromRow";
import PageHeader from "../../../components/PageHeader";
import exportIcon from "../../../static/assets/Exporter.svg";
import PdfImageSelectionModal from "../../activity/components/PdfImageSelectionModal";
import { getUniqueHistoryCycle } from "../../activity/utils/getUniqueHistoryCycle";
import { azureSASTokenToUrl } from "../../../components/formatted/FormattedAzureSasToken";

const getHistoryImageUrl = (item) =>
  item?.image_url ||
  (Array.isArray(item?.image_urls) ? item.image_urls.find(Boolean) : null) ||
  "";

const filterHistoryMaterialBySelectedUrls = (
  historyMaterial = [],
  selectedImageUrls,
) => {
  const filtered =
    !selectedImageUrls || selectedImageUrls.size === 0
      ? [...historyMaterial]
      : historyMaterial.filter((item) =>
          selectedImageUrls.has(getHistoryImageUrl(item)),
        );
  return filtered.sort(
    (a, b) =>
      new Date(b._time || 0).getTime() - new Date(a._time || 0).getTime(),
  );
};

const getAvailableReportOptionsByRegulation = (
  characterizationType,
  { isBenneCharacterization = false } = {},
) => {
  const isEpr = characterizationType === "EPR";
  const is1199 = characterizationType === "1199";
  const isClassic = characterizationType === "classic";
  return {
    mass: true,
    volume: !is1199 && !isEpr,
    outlet: !isEpr,
    comments: true,
    sizeDistribution: isClassic && !isBenneCharacterization,
  };
};

const STATUS_VARIANTS = {
  Validé: {
    icon: "fas fa-check",
    badgeClass: "bg-[#E9FBFB] text-[#2AB9B8]",
  },
  Validated: {
    icon: "fas fa-check",
    badgeClass: "bg-[#E9FBFB] text-[#2AB9B8]",
  },
  ImageEnTraitement: {
    icon: "fas fa-image",
    badgeClass: "bg-[#EFEFFF] text-[#5F5BFF]",
  },
  ACompleter: {
    icon: "fas fa-pen",
    badgeClass: "bg-[#E6ECFD] text-[#0136BD]",
  },
  CorrectionEnTraitement: {
    icon: "fas fa-tasks",
    badgeClass: "bg-[#FFF4E6] text-[#B8860B]",
  },
  "En traitement": {
    icon: "fas fa-history",
    badgeClass: "bg-[#EFEFFF] text-[#5F5BFF]",
  },
  "In progress": {
    icon: "fas fa-history",
    badgeClass: "bg-[#EFEFFF] text-[#5F5BFF]",
  },
  InProgress: {
    icon: "fas fa-history",
    badgeClass: "bg-[#EFEFFF] text-[#5F5BFF]",
  },
  "En cours": {
    icon: "fas fa-history",
    badgeClass: "bg-[#EFEFFF] text-[#5F5BFF]",
  },
  Erreur: {
    icon: "fas fa-exclamation-triangle",
    badgeClass: "bg-[#FBEBED] text-[#AF2F3C]",
    helperIcon: "fas fa-question-circle",
  },
  Error: {
    icon: "fas fa-exclamation-triangle",
    badgeClass: "bg-[#FBEBED] text-[#AF2F3C]",
    helperIcon: "fas fa-question-circle",
  },
  "A compléter": {
    icon: "fas fa-pen",
    badgeClass: "bg-[#E6ECFD] text-[#0136BD]",
  },
  "À compléter": {
    icon: "fas fa-pen",
    badgeClass: "bg-[#E6ECFD] text-[#0136BD]",
  },
};

const getStatusLabel = (status, t) => {
  if (status === "ImageEnTraitement") return t("Image en traitement");
  if (status === "ACompleter") return t("A compléter");
  if (status === "CorrectionEnTraitement") return t("Correction en traitement");
  return t(status);
};

const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const variant = STATUS_VARIANTS[status] ?? {
    icon: "fas fa-question-circle",
    badgeClass: "bg-gray-100 text-gray-600",
  };
  return (
    <span
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[14px] font-extrabold leading-[22px] ${variant.badgeClass}`}
    >
      <i className={`${variant.icon} text-xs text-current`} />
      {getStatusLabel(status, t)}
      {variant.helperIcon && (
        <i className={`${variant.helperIcon} text-xs text-[#5e5e5e]`} />
      )}
    </span>
  );
};

const HeaderBar = ({
  date,
  client,
  container,
  activeTab,
  setActiveTab,
  tabs = [],
  onTabChange,
  pdfRefs,
  period,
  superContainer,
  containers,
  zone,
  historyMaterial,
  totalWasteMass,
  totalMassRecoverable,
  totalWasteVolume,
  totalVolumeRecoverable,
  parentCategoriesList,
  materialDelivery,
  materialTotal,
  outletData,
  repData,
  characterizationType,
  isBenneCharacterization = false,
  files = [],
  selectedFilesForExport,
  setSelectedFilesForExport,
  setIsExporting,
  setExportProgress,
  setPdfFile,
  setOpenModalExport,
  setInitialPdfModalAction,
  totalFinesMass = 0,
  totalFinesVolume = 0,
  finesBelowThreshold = {},
  sizeThreshold = 40,
  isPageLoading = false,
  clientInfo = null,
  registrationClientCode,
  listSearch = "",
  fromAllReports = false,
  characterizationComments = [],
  useLatestCoefficients = false,
}) => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isPdfOptionsModalOpen, setIsPdfOptionsModalOpen] = useState(false);
  const [isPdfImageSelectionOpen, setIsPdfImageSelectionOpen] = useState(false);
  const [pdfOptionsAction, setPdfOptionsAction] = useState("download");
  const [pdfReportOptions, setPdfReportOptions] = useState({
    mass: false,
    volume: false,
    outlet: false,
    comments: false,
    sizeDistribution: false,
  });
  const [pendingPdfReportOptions, setPendingPdfReportOptions] = useState(null);
  const dropdownRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const waitForPdfRefs = (refs, maxAttempts = 150, intervalMs = 100) => {
    return new Promise((resolve) => {
      let attempts = 0;
      const check = () => {
        attempts++;
        const hasRef = [
          refs.massChartRef?.current,
          refs.massTableRef?.current,
          refs.volumeChartRef?.current,
          refs.objectsChartRef?.current,
          refs.outletChartRef?.current,
          refs.outletTableRef?.current,
          refs.repChartRef?.current,
          refs.repTableRef?.current,
          refs.sizeChartRef?.current,
          refs.sizeMatrixRef?.current,
        ].some(Boolean);
        if (hasRef || attempts >= maxAttempts) {
          resolve();
          return;
        }
        setTimeout(check, intervalMs);
      };
      check();
    });
  };

  const handleExportPdf = async (
    e,
    exportOptions = null,
    historyMaterialForPdf = historyMaterial,
  ) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsDropdownOpen(false);
    setIsPdfOptionsModalOpen(false);
    setIsPdfImageSelectionOpen(false);
    setIsExporting(true);
    const availableOptions = getAvailableReportOptionsByRegulation(
      characterizationType,
      {
        isBenneCharacterization,
      },
    );
    const normalizedOptions = {
      mass: Boolean(exportOptions?.mass) && availableOptions.mass,
      volume: Boolean(exportOptions?.volume) && availableOptions.volume,
      outlet: Boolean(exportOptions?.outlet) && availableOptions.outlet,
      comments: Boolean(exportOptions?.comments) && availableOptions.comments,
      sizeDistribution:
        Boolean(exportOptions?.sizeDistribution) &&
        availableOptions.sizeDistribution,
    };
    const reportProgress = (percent, labelKey) => {
      setExportProgress?.({ percent, label: labelKey ? t(labelKey) : "" });
    };
    reportProgress(5, "Loading...");

    try {
      if (activeTab !== "reports") {
        setActiveTab("reports");
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
      reportProgress(15, "Loading...");

      await waitForPdfRefs(pdfRefs);
      reportProgress(25, "Loading...");
      await new Promise((resolve) => setTimeout(resolve, 2500));

      reportProgress(45, "Loading...");
      const pdfClient = {
        ...(client || {}),
        ...(clientInfo?.clientParsed || {}),
        name:
          clientInfo?.clientParsed?.name && clientInfo.clientParsed.name !== "-"
            ? clientInfo.clientParsed.name
            : client?.name,
        address:
          clientInfo?.clientParsed?.address &&
          clientInfo.clientParsed.address !== "-"
            ? clientInfo.clientParsed.address
            : client?.address,
        clientUniqueId:
          clientInfo?.clientParsed?.clientUniqueId ||
          client?.clientUniqueId ||
          "",
      };
      const { pdfContent, pdfFilename } = await captureCharacterizationPdf({
        refs: pdfRefs,
        period,
        superContainer,
        container,
        historyMaterial: historyMaterialForPdf,
        totalWasteMass,
        totalMassRecoverable,
        totalWasteVolume,
        totalVolumeRecoverable,
        totalWeight: period?.weight ? period.weight : 0,
        client: pdfClient,
        registrationClientCode,
        wasteCategoryName: period?.waste_category || "-",
        parentCategoriesList,
        outletData,
        comments: characterizationComments.length
          ? characterizationComments
          : period?.comments || [],
        t,
        repData,
        characterizationType,
        sizeThreshold,
        reportOptions: normalizedOptions,
      });
      reportProgress(75, "Loading...");
      const mainPdfBlob = await pdf(pdfContent).toBlob();
      const pdfList = [{ filename: pdfFilename, pdfBlob: mainPdfBlob }];

      const filesToMerge =
        selectedFilesForExport?.length > 0
          ? selectedFilesForExport
          : files || [];
      if (filesToMerge.length > 0) {
        const azureFiles = await Promise.all(
          filesToMerge.map(async (filePath) => {
            const { data: fileBlob } = await getAzureBlobFile({
              blobUrl: `${process.env.REACT_APP_AZURE_URL}${filePath}`,
            });
            const fileName = filePath.split("/").pop();
            return { filename: fileName, pdfBlob: fileBlob };
          }),
        );
        pdfList.push(...azureFiles);
        const mergedPdfBlob = await mergePdfs(pdfList);

        reportProgress(100, "Loading...");
        await new Promise((resolve) => setTimeout(resolve, 400));

        const url = URL.createObjectURL(mergedPdfBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = pdfFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        reportProgress(100, "Loading...");
        await new Promise((resolve) => setTimeout(resolve, 400));

        const url = URL.createObjectURL(mainPdfBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = pdfFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
      setSelectedFilesForExport([]);
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error(t("Error generating PDF"));
    } finally {
      setIsExporting(false);
      setExportProgress?.({ percent: 0, label: "" });
    }
  };
  const handleExportCsv = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsDropdownOpen(false);
    setIsExporting(true);
    setExportProgress?.({ percent: 5, label: t("Loading...") });
    try {
      await exportCsvFromRow({
        period,
        container,
        superContainer,
        i18n,
        t,
        useLatestCoefficients,
        onProgress: ({ percent, label }) =>
          setExportProgress?.({
            percent,
            label: label || t("Loading..."),
          }),
      });
    } catch (error) {
      console.error("Error exporting CSV:", error);
    } finally {
      setIsExporting(false);
      setExportProgress?.({ percent: 0, label: "" });
    }
  };
  const handleExportEmail = async (
    e,
    exportOptions = null,
    historyMaterialForPdf = historyMaterial,
  ) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsDropdownOpen(false);
    setIsPdfOptionsModalOpen(false);
    setIsPdfImageSelectionOpen(false);
    setIsExporting(true);
    const availableOptions = getAvailableReportOptionsByRegulation(
      characterizationType,
      {
        isBenneCharacterization,
      },
    );
    const normalizedOptions = {
      mass: Boolean(exportOptions?.mass) && availableOptions.mass,
      volume: Boolean(exportOptions?.volume) && availableOptions.volume,
      outlet: Boolean(exportOptions?.outlet) && availableOptions.outlet,
      comments: Boolean(exportOptions?.comments) && availableOptions.comments,
      sizeDistribution:
        Boolean(exportOptions?.sizeDistribution) &&
        availableOptions.sizeDistribution,
    };
    const reportProgress = (percent, labelKey) => {
      setExportProgress?.({ percent, label: labelKey ? t(labelKey) : "" });
    };
    reportProgress(5, "Loading...");
    try {
      if (activeTab !== "reports") {
        setActiveTab("reports");
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
      reportProgress(15, "Loading...");
      await waitForPdfRefs(pdfRefs);
      reportProgress(25, "Loading...");
      await new Promise((resolve) => setTimeout(resolve, 2500));
      reportProgress(45, "Loading...");
      const azureUrl = process.env.REACT_APP_AZURE_URL;
      const pdfClient = {
        ...(client || {}),
        ...(clientInfo?.clientParsed || {}),
        name:
          clientInfo?.clientParsed?.name && clientInfo.clientParsed.name !== "-"
            ? clientInfo.clientParsed.name
            : client?.name,
        address:
          clientInfo?.clientParsed?.address &&
          clientInfo.clientParsed.address !== "-"
            ? clientInfo.clientParsed.address
            : client?.address,
        clientUniqueId:
          clientInfo?.clientParsed?.clientUniqueId ||
          client?.clientUniqueId ||
          "",
      };
      const { pdfContent, pdfFilename } = await captureCharacterizationPdf({
        refs: pdfRefs,
        period,
        superContainer,
        container,
        historyMaterial: historyMaterialForPdf,
        totalWasteMass,
        totalMassRecoverable,
        totalWasteVolume,
        totalVolumeRecoverable,
        totalWeight: period?.weight ? period.weight : 0,
        client: pdfClient,
        registrationClientCode,
        wasteCategoryName: period?.wasteCategoryName || "",
        parentCategoriesList,
        outletData,
        comments: characterizationComments.length
          ? characterizationComments
          : period?.comments || [],
        t,
        repData,
        characterizationType,
        sizeThreshold,
        reportOptions: normalizedOptions,
      });
      reportProgress(75, "Loading...");
      const mainPdfBlob = await pdf(pdfContent).toBlob();
      const pdfList = [{ filename: pdfFilename, pdfBlob: mainPdfBlob }];

      const filesToMerge =
        selectedFilesForExport?.length > 0
          ? selectedFilesForExport
          : files || [];
      if (filesToMerge.length > 0) {
        const azureFiles = await Promise.all(
          filesToMerge.map(async (filePath) => {
            const { data: fileBlob } = await getAzureBlobFile({
              blobUrl: `${azureUrl}${filePath}`,
            });
            const fileName = filePath.split("/").pop();
            return { filename: fileName, pdfBlob: fileBlob };
          }),
        );
        pdfList.push(...azureFiles);
        const mergedPdfBlob = await mergePdfs(pdfList);
        setPdfFile([{ filename: pdfFilename, pdfBlob: mergedPdfBlob }]);
      } else {
        setPdfFile(pdfList);
      }
      reportProgress(100, "Loading...");
      setInitialPdfModalAction("share");
      setOpenModalExport(true);
      setSelectedFilesForExport([]);
    } catch (error) {
      console.error("Error generating PDF for email:", error);
      toast.error(t("Error generating PDF"));
    } finally {
      setIsExporting(false);
      setExportProgress?.({ percent: 0, label: "" });
    }
  };

  const openPdfOptionsModal = (e, action = "download") => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const availableOptions = getAvailableReportOptionsByRegulation(
      characterizationType,
      {
        isBenneCharacterization,
      },
    );
    setPdfReportOptions({
      mass: false,
      volume: false,
      outlet: false,
      comments: false,
      sizeDistribution: false,
      ...availableOptions,
    });
    setPdfOptionsAction(action);
    setIsDropdownOpen(false);
    setIsPdfOptionsModalOpen(true);
  };

  const pdfImageSelectionData = useMemo(() => {
    const allImages = (historyMaterial || [])
      .map((item) => {
        const imageUrl = getHistoryImageUrl(item);
        if (!imageUrl) return null;
        return {
          item: {
            ...item,
            image_url: imageUrl,
          },
        };
      })
      .filter(Boolean)
      .sort(
        (a, b) =>
          new Date(b.item._time || 0).getTime() -
          new Date(a.item._time || 0).getTime(),
      );
    // Same preselection as compliance report (filling delta, first/last, sorting errors).
    const defaultSelectedUrls = new Set(
      getUniqueHistoryCycle(allImages.map(({ item }) => item)).map(
        (item) => item.image_url,
      ),
    );
    return { allImages, defaultSelectedUrls };
  }, [historyMaterial]);

  const openPdfImageSelection = (exportOptions) => {
    setPendingPdfReportOptions(exportOptions);
    setIsPdfOptionsModalOpen(false);
    // Skip image picker when there is nothing meaningful to choose
    // (applies to all characterization types, not only "en benne").
    if ((historyMaterial || []).length <= 1) {
      if (pdfOptionsAction === "email") {
        handleExportEmail(null, exportOptions, historyMaterial);
        return;
      }
      handleExportPdf(null, exportOptions, historyMaterial);
      return;
    }
    setIsPdfImageSelectionOpen(true);
  };

  const handleConfirmPdfImageSelection = (selectedImageUrls) => {
    const exportOptions = pendingPdfReportOptions || pdfReportOptions;
    const filteredHistory = filterHistoryMaterialBySelectedUrls(
      historyMaterial,
      selectedImageUrls,
    );
    setPendingPdfReportOptions(null);
    if (pdfOptionsAction === "email") {
      handleExportEmail(null, exportOptions, filteredHistory);
      return;
    }
    handleExportPdf(null, exportOptions, filteredHistory);
  };

  const handleAllClientCharacterizations = () => {
    const parseClient = (payload) => {
      if (!payload) return { name: "" };
      try {
        return typeof payload === "string" ? JSON.parse(payload) : payload;
      } catch {
        return { name: "" };
      }
    };
    const clientParsed =
      period?.clientParsed ||
      (period?.client ? parseClient(period.client) : null) ||
      client ||
      {};
    const clientName = clientParsed?.name || client?.name || "";
    if (zone?._id) {
      const now = new Date();
      now.setHours(23, 59, 59, 999);
      const sinceBeginningStart = new Date(2021, 3, 26);
      sinceBeginningStart.setHours(0, 0, 0, 0);
      const periodStart = sinceBeginningStart.toISOString();
      const periodEnd = now.toISOString();
      const listPath = `/characterization/list/${zone._id}?period=sinceBeginning&start=${encodeURIComponent(periodStart)}&end=${encodeURIComponent(periodEnd)}`;
      history.push(listPath, {
        zone,
        superContainer,
        containers,
        clientFilter: clientName,
        periodStartDate: periodStart,
        periodEndDate: periodEnd,
        periodDisplayLabel: t("Since the beginning"),
      });
    } else {
      history.goBack();
    }
  };

  const wasteOriginName = clientInfo?.clientParsed?.name;
  const wasteName = clientInfo?.wasteCategory?.wasteCategoryName;
  const weightValue = clientInfo?.weight;

  const hasWasteOrigin = Boolean(wasteOriginName && wasteOriginName !== "-");
  const hasWasteName = Boolean(wasteName && wasteName !== "-");
  const hasWeight =
    weightValue != null &&
    !Number.isNaN(Number(weightValue)) &&
    Number(weightValue) > 0;

  const canExport = hasWasteOrigin && hasWeight && hasWasteName;

  useEffect(() => {
    if (!canExport) {
      setIsDropdownOpen(false);
    }
  }, [canExport]);

  const actions = (
    <div className="flex flex-row items-center gap-2">
      <button
        className="char-button"
        onClick={handleAllClientCharacterizations}
      >
        <i className="fas fa-history text-sm" />
        <span>{t("All client characterizations")}</span>
      </button>
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() =>
            !isPageLoading && canExport && setIsDropdownOpen(!isDropdownOpen)
          }
          className="char-button char-button-primary"
          disabled={isPageLoading || !canExport}
        >
          <img
            src={exportIcon}
            alt="export"
            className="char-button-icon"
            style={{ filter: "brightness(0) invert(1)" }}
          />
          <span>{t("Export")}</span>
          <i className="fas fa-chevron-down text-xs" />
        </button>
        {isDropdownOpen && (
          <div className="char-dropdown">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!canExport) return;
                openPdfOptionsModal(e, "download");
              }}
              className="char-dropdown-item"
            >
              <img
                src={exportIcon}
                alt="export"
                className="char-dropdown-item-icon"
              />
              <span>Pdf</span>
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!canExport) return;
                handleExportCsv(e);
              }}
              className="char-dropdown-item"
            >
              <img
                src={exportIcon}
                alt="export"
                className="char-dropdown-item-icon"
              />
              <span>Csv</span>
            </button>
            <div className="char-dropdown-item-divider" />
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!canExport) return;
                openPdfOptionsModal(e, "email");
              }}
              className="char-dropdown-item"
            >
              <i className="fas fa-envelope char-text-secondary" />
              <span>{t("Send by email")}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const calculatedStatus = getCharacterizationStatus(period);

  const handleBackClick = () => {
    const target = getCharacterizationDetailBackTarget({
      zone,
      listSearch,
      fromAllReports,
      superContainer,
      containers,
    });
    if (target) {
      history.push(target.path, target.state);
      return;
    }
    history.goBack();
  };

  const exportIconGray = (
    <img
      src={exportIcon}
      alt=""
      className="w-4 h-4"
      style={{ filter: "brightness(0) invert(0.45)" }}
    />
  );
  const iconGray = "text-[#6B7280]";
  const actionsMenuItems = [
    {
      label: t("All client characterizations"),
      icon: <i className={`fas fa-history ${iconGray}`} aria-hidden />,
      onClick: handleAllClientCharacterizations,
    },
    {
      label: "Pdf",
      icon: exportIconGray,
      onClick: (e) => {
        if (!canExport) return;
        openPdfOptionsModal(e);
      },
    },
    {
      label: "Csv",
      icon: exportIconGray,
      onClick: (e) => {
        if (!canExport) return;
        handleExportCsv(e);
      },
    },
    {
      label: t("Send by email"),
      icon: <i className={`fas fa-envelope ${iconGray}`} aria-hidden />,
      onClick: (e) => {
        if (!canExport) return;
        openPdfOptionsModal(e, "email");
      },
    },
  ];

  const titleContent = (
    <div className="flex items-center gap-3 flex-wrap">
      <span>{date}</span>
      <span className="char-badge char-badge-outline">{client?.name}</span>
      <StatusBadge status={calculatedStatus} />
    </div>
  );

  const availableReportOptions = getAvailableReportOptionsByRegulation(
    characterizationType,
    { isBenneCharacterization },
  );

  return (
    <>
      <PageHeader
        showBackButton
        onBackClick={handleBackClick}
        backLabel={`${t("Back to")} ${fromAllReports ? t("Characterizations") : container?.addressName || t("Center")}`}
        titleContent={titleContent}
        titleContentMain={<span>{date}</span>}
        titleContentBadges={
          <>
            <span className="char-badge char-badge-outline">
              {client?.name}
            </span>
            <StatusBadge status={calculatedStatus} />
          </>
        }
        actions={actions}
        actionsMenuItems={actionsMenuItems}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={onTabChange}
      />
      <Modal
        isOpen={isPdfOptionsModalOpen}
        centered
        className="char-export-options-modal-dialog"
        contentClassName="char-export-options-modal-content rounded-lg"
      >
        <ModalHeader
          toggle={() => setIsPdfOptionsModalOpen(false)}
          className="border-b border-[#ECECEC] px-6 py-3"
        >
          <div className="pl-2.5 pr-4 text-[18px] leading-[1.35] font-semibold text-[#1F2937] mt-2">
            {characterizationType === "classic"
              ? t("Characterizations without regulation")
              : `${t("Characterizations with regulation")} ${
                  characterizationType === "1199"
                    ? `${t("Decree")} n° 2021-1199`
                    : characterizationType === "EPR"
                      ? t("France EPR recycling channel")
                      : characterizationType === "RHW"
                        ? t("RHW")
                        : ""
                }`}
          </div>
        </ModalHeader>
        <ModalBody
          className="px-6 py-5 flex flex-col gap-4"
          style={{ marginLeft: "-5px" }}
        >
          {availableReportOptions.mass && (
            <label className="w-full flex flex-row items-center gap-3 m-0 cursor-pointer">
              <input
                className="m-0 h-4 w-4 accent-[#00A7A6] shrink-0"
                type="checkbox"
                checked={pdfReportOptions.mass}
                onChange={(event) =>
                  setPdfReportOptions((prev) => ({
                    ...prev,
                    mass: event.target.checked,
                  }))
                }
              />
              <span className="text-[15px] leading-[1.35] text-[#3F3F46]">
                {t("Add mass analysis to the report")}
              </span>
            </label>
          )}
          {availableReportOptions.volume && (
            <label className="w-full flex flex-row items-center gap-3 m-0 cursor-pointer">
              <input
                className="m-0 h-4 w-4 accent-[#00A7A6] shrink-0"
                type="checkbox"
                checked={pdfReportOptions.volume}
                onChange={(event) =>
                  setPdfReportOptions((prev) => ({
                    ...prev,
                    volume: event.target.checked,
                  }))
                }
              />
              <span className="text-[15px] leading-[1.35] text-[#3F3F46]">
                {t("Add volume analysis to the report")}
              </span>
            </label>
          )}
          {availableReportOptions.outlet && (
            <label className="w-full flex flex-row items-center gap-3 m-0 cursor-pointer">
              <input
                className="m-0 h-4 w-4 accent-[#00A7A6] shrink-0"
                type="checkbox"
                checked={pdfReportOptions.outlet}
                onChange={(event) =>
                  setPdfReportOptions((prev) => ({
                    ...prev,
                    outlet: event.target.checked,
                  }))
                }
              />
              <span className="text-[15px] leading-[1.35] text-[#3F3F46]">
                {t("Add outlet distribution to the report")}
              </span>
            </label>
          )}
          {availableReportOptions.comments && (
            <label className="w-full flex flex-row items-center gap-3 m-0 cursor-pointer">
              <input
                className="m-0 h-4 w-4 accent-[#00A7A6] shrink-0"
                type="checkbox"
                checked={pdfReportOptions.comments}
                onChange={(event) =>
                  setPdfReportOptions((prev) => ({
                    ...prev,
                    comments: event.target.checked,
                  }))
                }
              />
              <span className="text-[15px] leading-[1.35] text-[#3F3F46]">
                {t("Add comments to the report")}
              </span>
            </label>
          )}
          {availableReportOptions.sizeDistribution && (
            <label className="w-full flex flex-row items-center gap-3 m-0 cursor-pointer">
              <input
                className="m-0 h-4 w-4 accent-[#00A7A6] shrink-0"
                type="checkbox"
                checked={pdfReportOptions.sizeDistribution}
                onChange={(event) =>
                  setPdfReportOptions((prev) => ({
                    ...prev,
                    sizeDistribution: event.target.checked,
                  }))
                }
              />
              <span className="text-[15px] leading-[1.35] text-[#3F3F46]">
                {t("Add size distribution to the report")}
              </span>
            </label>
          )}
        </ModalBody>
        <ModalFooter className="border-t border-[#ECECEC] px-6 py-4 flex justify-end gap-3">
          <button
            type="button"
            className="h-[40px] px-5 rounded-full border border-[#D4D4D8] bg-white text-[#27272A] text-sm font-medium"
            onClick={() => setIsPdfOptionsModalOpen(false)}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="h-[40px] px-5 rounded-full text-sm font-medium text-white bg-[#00A7A6] hover:opacity-95"
            onClick={() => {
              openPdfImageSelection(pdfReportOptions);
            }}
          >
            {pdfImageSelectionData.allImages.length > 1
              ? t("Next")
              : t("generateReport")}
          </button>
        </ModalFooter>
      </Modal>
      <PdfImageSelectionModal
        isOpen={isPdfImageSelectionOpen}
        onClose={() => {
          setIsPdfImageSelectionOpen(false);
          setPendingPdfReportOptions(null);
        }}
        allImages={pdfImageSelectionData.allImages}
        defaultSelectedUrls={pdfImageSelectionData.defaultSelectedUrls}
        azureSASTokenToUrl={azureSASTokenToUrl}
        onConfirm={handleConfirmPdfImageSelection}
        t={t}
        description={t("pdfImageSelectionDescriptionCharacterization")}
      />
    </>
  );
};

HeaderBar.propTypes = {
  useLatestCoefficients: PropTypes.bool,
};

export default HeaderBar;
